import { EVehicleType } from '@okcargo-apps/shared-domain';
import {
  ECapabilities,
  EMailingPeriodicity,
  ERoles,
  IAccount,
  IAccountPreferences,
  ICompany,
  ICostCenter,
  IOldQuestionnaire,
  IPreferenceRoutes,
} from '@okcargo-apps/shared-domain-ionic';
import {
  AccountResponse,
  CollaboratorQuestionairResponse,
  CompanyResponse,
  SiteResponse,
} from '@okcargo/query-processor';

export function transformAccountResponseToIAccount(
  accountResponse: AccountResponse,
  companyResponse: CompanyResponse,
  sitesResponse: SiteResponse[],
  account: IAccount,
): IAccount {
  // The account may come from a previous session (restored from storage) or from a previous
  // refresh: capabilities and role must be recomputed from scratch, never accumulated.
  account.capabilities = [];
  account.role = ERoles.DEFAULT;

  account.capabilities.push(ECapabilities.AUTHENTICATED);

  account.id = (accountResponse.contactId as any) ?? account.id;
  account.firstName = accountResponse.authUserPayload.firstName ?? account.firstName;
  account.lastName = accountResponse.authUserPayload.lastName ?? account.lastName;
  account.email = accountResponse.authUserPayload.email ?? account.email;
  account.username = accountResponse.username ?? account.username; // TODO review
  account.privacyPolicyAccepted = true; // TODO Review accountResponse.privacyPolicyAccepted ?? account.privacyPolicyAccepted;

  account.lang = 'es'; // TODO Review typeof accountResponse.langKey === 'string' ? accountResponse.langKey.toLowerCase() : account.lang;

  account.contactId = accountResponse.contactId ?? account.contactId;
  account.phone = accountResponse.authUserPayload.phone ?? account.phone;

  account.company = getICompany(companyResponse);
  account.company.costCenters = transformFromSitesToICostCenters(sitesResponse);

  const authorities = accountResponse.authUserPayload.capabilities;

  if (Array.isArray(authorities)) {
    if (authorities.includes('ROLE_CARRIER_ADMIN')) {
      handleAdministrativeRoles(companyResponse, account);
    }
    if (authorities.includes('ROLE_DRIVER')) {
      handleDriverRoles(authorities, account);
    }
    if (authorities.includes('ROLE_COMPANY_ADMIN')) {
      //handleAdministrativeRole(companyResponse, account);
      handleShipperRole(companyResponse, account);
      if (authorities.includes('GREEN')) {
        account.capabilities.push(ECapabilities.SUSTAINABILITY);
      }
    }

    if (authorities.includes('ROLE_DCD_CARRIER')) {
      handleDecaAsCarrierCapabilities(account);
    }
    if (authorities.includes('ROLE_DCD_SHIPPER')) {
      handleDecaAsShipperCapabilities(account);
    }
    if (authorities.includes('ROLE_DCD_DRIVER')) {
      handleDecaAsDriverCapabilities(account);
    }
  }

  return { ...account };
}

function handleAdministrativeRoles(company: CompanyResponse, account: IAccount): IAccount {
  account.role = ERoles.ADMINISTRATIVE;
  account.capabilities.push(
    ECapabilities.ADMINISTRATIVE_DOCUMENTS,
    ECapabilities.CRUD_VEHICLES,
    ECapabilities.VIEW_PRICES,
    ECapabilities.VIEW_DRIVERS,
    ECapabilities.ASSIGN_DRIVERS,
    ECapabilities.VIEW_MILEAGES, // TODO review this
  );
  if (company.isCarrierCompany) {
    account.capabilities.push(ECapabilities.CRUD_USERS_CARRIER);
  }
  if (account.company.isMarketAllowed) {
    account.capabilities.push(ECapabilities.VIEW_MARKET);
  }

  // if (company.isShipperCompany) {
  //   account.capabilities.push(ECapabilities.UPLOAD_FREIGHTS);
  // }

  return account;
}

function handleDriverRoles(auhtorities: string[], account: IAccount): IAccount {
  account.capabilities.push(ECapabilities.INTERACT_WITH_STOPS);
  account.capabilities.push(ECapabilities.VIEW_MILEAGES); // TODO review this
  if (account.role === ERoles.ADMINISTRATIVE) {
    account.role = ERoles.DRIVER_ADMINISTRATIVE;
  } else {
    account.role = ERoles.DRIVER;
    if (Array.isArray(auhtorities) && auhtorities.includes('MARKET_ALLOWED_TO_DRIVERS')) {
      account.capabilities.push(ECapabilities.VIEW_MARKET);
    }
  }
  return account;
}

function handleShipperRole(company: CompanyResponse, account: IAccount): IAccount {
  account.role = ERoles.ADMINISTRATIVE;
  account.capabilities.push(ECapabilities.ADMINISTRATIVE_DOCUMENTS, ECapabilities.VIEW_PRICES);
  if (company.isShipperCompany) {
    account.capabilities.push(ECapabilities.CRUD_USERS_SHIPPER);
    account.capabilities.push(ECapabilities.UPLOAD_FREIGHTS);
    //account.company.costCenters = transformToICostCenters(companyAsShipper);
  }

  return account;
}

function handleDecaAsCarrierCapabilities(account: IAccount): IAccount {
  account.capabilities.push(ECapabilities.VIEW_DECA_AS_CARRIER);
  account.capabilities.push(ECapabilities.CREATE_DECA_AS_CARRIER);
  account.capabilities.push(ECapabilities.CRUD_USERS_DECA_CARRIER);

  account.capabilities.push(ECapabilities.MODIFY_DECA_RESERVATIONS);
  account.capabilities.push(ECapabilities.MODIFY_DECA_VEHICLES);
  account.capabilities.push(ECapabilities.MODIFY_DECA_DATES);

  return account;
}

function handleDecaAsDriverCapabilities(account: IAccount): IAccount {
  account.capabilities.push(ECapabilities.VIEW_DECA_AS_CARRIER);
  account.capabilities.push(ECapabilities.VIEW_DECA_AS_DRIVER);

  account.capabilities.push(ECapabilities.MODIFY_DECA_RESERVATIONS);
  account.capabilities.push(ECapabilities.MODIFY_DECA_VEHICLES);
  account.capabilities.push(ECapabilities.MODIFY_DECA_DATES);

  return account;
}

function handleDecaAsShipperCapabilities(account: IAccount): IAccount {
  account.capabilities.push(ECapabilities.VIEW_DECA_AS_SHIPPER);
  account.capabilities.push(ECapabilities.CREATE_DECA_AS_SHIPPER);
  account.capabilities.push(ECapabilities.CRUD_USERS_DECA_SHIPPER);

  account.capabilities.push(ECapabilities.MODIFY_DECA_RESERVATIONS);
  account.capabilities.push(ECapabilities.MODIFY_DECA_LOCATIONS);
  account.capabilities.push(ECapabilities.MODIFY_DECA_GOODS_DATA);

  return account;
}

export function getICompany(company: CompanyResponse): ICompany {
  const isInternational = /^[a-zA-Z]{2}/.test(company.taxId);
  const isNotES = !/^ES$/i.test(company.taxId.substring(0, 2));

  return {
    id: company.id as any,
    name: company.name,
    taxNumber: company.taxId,
    streetAddress: company.addressPayload.street,
    postalCode: company.addressPayload.postalCode,
    city: company.addressPayload.city,
    stateCode: company.addressPayload.stateCode,
    countryCode: company.addressPayload.countryCode,
    phone: company.phone,
    // TODO
    activated: true, // TODO Review company.activated,
    isInternational: isInternational && isNotES,
    isMarketAllowed: company.carrierReliability === 0 ? false : true,
    decaCredits: company.availableDcdCredits ?? 0,
  };
}

export function transformFromSitesToICostCenters(sites: SiteResponse[]): ICostCenter[] {
  const costCenters = sites.map((site: SiteResponse) => ({
    id: site.id,
    name: site.name,
  }));

  return costCenters;
}

function populateInitialAccountPreferences(
  companyResponse: any,
  accountPreferences: IAccountPreferences,
): IAccountPreferences {
  // console.log('populateInitialAccountPreferences', companyResponse);
  // console.log('populateInitialAccountPreferences', accountPreferences);
  if (companyResponse.sendMarketEmail === false) {
    accountPreferences.emailPeriodicity = EMailingPeriodicity.NEVER;
  } else if (companyResponse.emailPeriodicity) {
    accountPreferences.emailPeriodicity =
      EMailingPeriodicity[companyResponse.emailPeriodicity as keyof typeof EMailingPeriodicity];
  }

  // TODO remove? comes from previous questionnaire / preferences for company (not account)
  // if (companyResponse.company?.phone) {
  //   accountPreferences.contactPhone = companyResponse.company.phone;
  // }
  // if (accountResponse.email) {
  //   accountPreferences.contactEmail = accountResponse.email;
  // }

  return accountPreferences;
}

export function transformToIAccountPreferences(
  accountPreferencesBaseResponse: CollaboratorQuestionairResponse,
  accountPreferencesRoutesResponse: any,
  companyResponse: any,
  contactResponse: any,
  accountPreferences: IAccountPreferences,
  userEmail: string,
): IAccountPreferences {
  //console.log(accountPreferencesBaseResponse);
  // console.log(accountPreferencesRoutesResponse);

  // TODO Keep only for compatibility with the old questionnaire. In the future, remove
  const oldQuestionnaire = keepOldQuestionnaire(accountPreferencesBaseResponse);
  if (Object.keys(oldQuestionnaire).length > 0) {
    accountPreferences.oldQuestionnaire = oldQuestionnaire;
  }

  if (accountPreferencesBaseResponse?.id !== undefined) {
    accountPreferences.id = accountPreferencesBaseResponse.id;
  }

  // TODO remove? comes from old questionnaire

  // if (accountPreferencesBaseResponse?.futureContact) {
  //   accountPreferencesBaseResponse.futureContact.forEach((contactType: string) => {
  //     switch (contactType.toUpperCase()) {
  //       case 'EMAIL':
  //         accountPreferences.consentEmail = true;
  //         break;
  //       case 'WHATSAPP':
  //         accountPreferences.consentWhatsApp = true;
  //         break;
  //       case 'PHONE':
  //         accountPreferences.consentPhone = true;
  //         break;
  //     }
  //   });
  // }

  if (accountPreferencesBaseResponse?.freightsPerWeek !== undefined) {
    accountPreferences.freightsPerWeek = accountPreferencesBaseResponse.freightsPerWeek;
  }
  if (accountPreferencesBaseResponse?.canvasNumber !== undefined) {
    accountPreferences.canvasNumber = accountPreferencesBaseResponse.canvasNumber;
  }
  if (accountPreferencesBaseResponse?.boxNumber !== undefined) {
    accountPreferences.boxNumber = accountPreferencesBaseResponse.boxNumber;
  }
  if (accountPreferencesBaseResponse?.frigoNumber !== undefined) {
    accountPreferences.frigoNumber = accountPreferencesBaseResponse.frigoNumber;
  }
  if (accountPreferencesBaseResponse?.boxNumber !== undefined) {
    accountPreferences.megaNumber = accountPreferencesBaseResponse.megaNumber;
  }

  if (accountPreferencesBaseResponse?.whatsappContact !== undefined) {
    accountPreferences.contactWhatsApp = accountPreferencesBaseResponse.whatsappContact;
  } else if (oldQuestionnaire.whatsappCommunicationsPhone) {
    accountPreferences.contactWhatsApp = oldQuestionnaire.whatsappCommunicationsPhone;
  }
  if (accountPreferencesBaseResponse?.phoneContact !== undefined) {
    accountPreferences.contactPhone = accountPreferencesBaseResponse.phoneContact;
  } else if (companyResponse?.phone) {
    accountPreferences.contactPhone = companyResponse?.phone;
  }
  if (accountPreferencesBaseResponse?.emailContact !== undefined) {
    accountPreferences.contactEmail = accountPreferencesBaseResponse.emailContact;
  } else if (userEmail) {
    accountPreferences.contactEmail = userEmail;
  }

  if (accountPreferencesBaseResponse?.emailContactConsentAt !== undefined) {
    accountPreferences.consentEmail = true;
  }
  if (accountPreferencesBaseResponse?.phoneContactConsentAt !== undefined) {
    accountPreferences.consentPhone = true;
  }
  if (accountPreferencesBaseResponse?.whatsappContactConsentAt !== undefined) {
    accountPreferences.consentWhatsApp = true;
  }

  // if (accountPreferencesBaseResponse?.vehicleTypes?.length) {
  //   accountPreferences.vehicleTypes = [];
  //   for (const type of accountPreferencesBaseResponse.vehicleTypes) {
  //     if (Object.values(EVehicleType).includes(type as EVehicleType)) {
  //       accountPreferences.vehicleTypes.push(type as EVehicleType);
  //     }
  //   }
  // }

  accountPreferences.routes = accountPreferencesRoutesResponse.map(
    (response: any) =>
      ({
        id: response.id,
        vehicleType: response.vehicleType as EVehicleType,
        originCountryIsoCode: response.originCountryIsoCode,
        originStateCode: response.originPostalCode,
        destinationCountryIsoCode: response.destinationCountryIsoCode,
        destinationStateCode: response.destinationPostalCode,
      }) as IPreferenceRoutes,
  );

  if (companyResponse?.company?.sendMarketEmail === false) {
    accountPreferences.emailPeriodicity = EMailingPeriodicity.NEVER;
  } else if (companyResponse?.company?.emailPeriodicity) {
    accountPreferences.emailPeriodicity =
      EMailingPeriodicity[companyResponse?.company?.emailPeriodicity as keyof typeof EMailingPeriodicity];
  }

  if (companyResponse?.sendMarketEmail === false) {
    accountPreferences.emailPeriodicity = EMailingPeriodicity.NEVER;
  } else if (contactResponse?.emailPeriodicity) {
    accountPreferences.emailPeriodicity =
      EMailingPeriodicity[contactResponse.emailPeriodicity as keyof typeof EMailingPeriodicity];
  }

  // console.log('accountPreferences in transformToIAccountPreferences()', accountPreferences);

  return accountPreferences;
}

function keepOldQuestionnaire(accountPreferencesBaseResponse: CollaboratorQuestionairResponse): IOldQuestionnaire {
  const oldQuestionnaire = {} as IOldQuestionnaire;
  if (accountPreferencesBaseResponse?.carrierOrAgency !== undefined) {
    oldQuestionnaire.carrierOrAgency = accountPreferencesBaseResponse?.carrierOrAgency;
  }
  if (accountPreferencesBaseResponse?.interestedOnSameLines !== undefined) {
    oldQuestionnaire.interestedOnSameLines = accountPreferencesBaseResponse?.interestedOnSameLines;
  }
  if (accountPreferencesBaseResponse?.firstContact !== undefined) {
    oldQuestionnaire.firstContact = accountPreferencesBaseResponse?.firstContact;
  }
  if (accountPreferencesBaseResponse?.futureContact !== undefined) {
    oldQuestionnaire.futureContact = accountPreferencesBaseResponse?.futureContact;
  }
  if (accountPreferencesBaseResponse?.isUsefullAcceptFreightWithLink !== undefined) {
    oldQuestionnaire.isUsefullAcceptFreightWithLink = accountPreferencesBaseResponse?.isUsefullAcceptFreightWithLink;
  }
  if (accountPreferencesBaseResponse?.receiveAllFreights !== undefined) {
    oldQuestionnaire.receiveAllFreights = accountPreferencesBaseResponse?.receiveAllFreights;
  }
  if (accountPreferencesBaseResponse?.exclusiveCarrier !== undefined) {
    oldQuestionnaire.exclusiveCarrier = accountPreferencesBaseResponse?.exclusiveCarrier;
  }
  if (accountPreferencesBaseResponse?.numberOfVehicles !== undefined) {
    oldQuestionnaire.numberOfVehicles = accountPreferencesBaseResponse?.numberOfVehicles;
  }
  if (accountPreferencesBaseResponse?.vehicleTypes !== undefined) {
    oldQuestionnaire.vehicleTypes = accountPreferencesBaseResponse?.vehicleTypes;
  }
  if (accountPreferencesBaseResponse?.whatsappCommunicationsPhone !== undefined) {
    oldQuestionnaire.whatsappCommunicationsPhone = accountPreferencesBaseResponse?.whatsappCommunicationsPhone;
  }
  if (accountPreferencesBaseResponse?.comments !== undefined) {
    oldQuestionnaire.comments = accountPreferencesBaseResponse?.comments;
  }

  return oldQuestionnaire;
}
